/**
 * The V0 game. Cold open (§6): no framing text, a one-time control hint,
 * the map, and whoever is on the stoop.
 *
 * `/ui` reads state and never computes it (§19). Every rule the screen
 * seems to know — who's here, what you can do, what you make of someone —
 * is a query into `/sim`. This file orchestrates: it sequences actions the
 * way a hand on a joystick does, it chooses words for reports, and it
 * keeps the one ongoing save.
 */

import {
  type Action,
  affordances,
  type BeatLine,
  type CharacterSheet,
  createSeed,
  dayOfWeek,
  digestContext,
  generateWorld,
  loadContent,
  type MapPoint,
  needsStatus,
  type NpcId,
  openerContext,
  renderContext,
  type SceneEnd,
  type Seedable,
  SIM_VERSION,
  type StepResult,
  TUNING,
  type ViolenceVerb,
  type WorldState,
  type WorldTime,
} from '@sim/index.ts';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Chargen } from './Chargen.tsx';
import { HINT, hintSeen, markHintSeen } from './hint.ts';
import { Joystick } from './Joystick.tsx';
import { Map } from './Map.tsx';
import { askModel, faultLabel, faultWords, type ModelFault, type ModelStatus, modelStatus, probeModel } from './model.ts';
import { clearSave, loadSave, type Save, SAVE_FORMAT, saver } from './persist.ts';
import { ReadScreen } from './ReadScreen.tsx';
import { afterExchange, billedMinutes, resumeTalking, startTalking, type Talking } from './talk.ts';
import { Talk } from './Talk.tsx';
import { useWorld } from './useWorld.ts';

const content = loadContent();

interface Line {
  readonly kind: 'prose' | 'said' | 'aside' | 'hint';
  readonly text: string;
}

function seedFromUrl(): Seedable | null {
  const param = new URLSearchParams(window.location.search).get('seed');
  if (param === null || param === '') return null;
  return /^\d+$/.test(param) ? Number(param) : param;
}

function putSeedInUrl(seed: Seedable): void {
  const url = new URL(window.location.href);
  url.searchParams.set('seed', String(seed));
  window.history.replaceState(null, '', url);
}

type Phase =
  | { readonly kind: 'loading' }
  | { readonly kind: 'make'; readonly seed: Seedable }
  | { readonly kind: 'play'; readonly state: WorldState; readonly lines: readonly Line[] };

export function App() {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  // One ongoing save. A seed in the URL that differs from the save's is a
  // request for a different district; otherwise the save wins.
  useEffect(() => {
    let cancelled = false;
    void loadSave().then((save) => {
      if (cancelled) return;
      const asked = seedFromUrl();
      const usable = save !== null && save.simVersion === SIM_VERSION && save.contentVersion === content.manifest.contentVersion;
      if (usable && (asked === null || String(asked) === String(save.seed))) {
        putSeedInUrl(save.seed);
        setPhase({ kind: 'play', state: save.state, lines: save.lines as Line[] });
        return;
      }
      const seed = asked ?? createSeed();
      putSeedInUrl(seed);
      setPhase({ kind: 'make', seed });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (phase.kind === 'loading') return null;
  if (phase.kind === 'make') {
    return (
      <Chargen
        content={content}
        seed={phase.seed}
        onDone={(sheet: CharacterSheet) => {
          const world = generateWorld(phase.seed, sheet, content);
          const lines: Line[] = [
            ...world.openingLines.map((text) => ({ kind: 'prose' as const, text })),
            ...(hintSeen() ? [] : [{ kind: 'hint' as const, text: HINT }]),
          ];
          setPhase({ kind: 'play', state: world.state, lines });
        }}
      />
    );
  }
  return <Play initial={phase.state} initialLines={phase.lines} />;
}

// ---------------------------------------------------------------------------

const WALK_STEP = 0.16;
const WALK_TICK_MS = 110;
const ARRIVED = 0.08;

function Play({ initial, initialLines }: { initial: WorldState; initialLines: readonly Line[] }) {
  const { state, dispatch } = useWorld(initial);
  const [lines, setLines] = useState<Line[]>([...initialLines]);
  const [reading, setReading] = useState<NpcId | null>(null);
  const [walkingTo, setWalkingTo] = useState<MapPoint | null>(null);
  // The model layer (§19): on, off, or not yet asked. Off means the sim speaks.
  const [model, setModel] = useState<ModelStatus>(modelStatus());
  useEffect(() => {
    if (modelStatus() === 'unknown') void probeModel().then(setModel);
  }, []);
  // A conversation in progress. The transcript itself lives in sim state
  // (`activeConversation`), so it survives a reload; this just tracks which
  // NPC and whether the first, chosen-length exchange has already been
  // charged. Reconnect on load if a scene was still open when we last saved
  // — see ui/talk.ts's resumeTalking for why `charged` is derived from the
  // transcript rather than assumed.
  const [talking, setTalking] = useState<Talking | null>(() =>
    initial.activeConversation
      ? resumeTalking(initial.activeConversation.npc, initial.activeConversation.transcript.length, TUNING.talk.wordMinutes)
      : null,
  );
  const talkingRef = useRef<Talking | null>(null);
  talkingRef.current = talking;
  const [busy, setBusy] = useState(false);
  /**
   * A narrator that is not working says so, once, in the player's words —
   * and the tab keeps saying it. Silence here is what let a total outage
   * pass for "the writing is a bit flat" across several sessions.
   */
  const toldFault = useRef(false);
  const noteFault = useCallback(
    (fault: ModelFault) => {
      setModel(fault);
      if (toldFault.current) return;
      toldFault.current = true;
      setLines((current) => [...current.slice(-12), { kind: 'aside', text: faultWords(fault) }]);
    },
    [],
  );
  // Prose arriving token by token. It lives outside `lines` until it is
  // finished, so a reply the sim rejects can be taken back cleanly.
  const [streaming, setStreaming] = useState('');
  const streamProse = useCallback((delta: string) => setStreaming((s) => s + delta), []);
  const streamReset = useCallback(() => setStreaming(''), []);
  const vector = useRef<{ x: number; y: number } | null>(null);
  const seenPlaces = useRef(new Set<string>([initial.player.position.at ?? '']));
  const stateRef = useRef(state);
  stateRef.current = state;

  // Autosave: every commit, debounced.
  const save = useRef(saver());
  useEffect(() => {
    const record: Save = {
      format: SAVE_FORMAT,
      simVersion: SIM_VERSION,
      contentVersion: state.contentVersion,
      seed: state.seed,
      state,
      lines,
    };
    save.current(record);
  }, [state, lines]);

  // Follow new lines, but let the cold open be read from the top.
  const linesEl = useRef<HTMLDivElement>(null);
  const opened = useRef(false);
  useEffect(() => {
    const el = linesEl.current;
    if (el && opened.current) el.scrollTop = el.scrollHeight;
    opened.current = true;
  }, [lines, streaming]);

  const say = useCallback((...added: Line[]) => {
    setLines((current) => [...current.filter((l) => l.kind !== 'hint').slice(-12), ...added]);
    markHintSeen();
  }, []);

  // The scene's counterpart, remembered across the beat that closes it.
  const lastSceneName = useRef('');

  /** Words for what a step did. Reports are data; this is the only place they become prose. */
  const narrate = useCallback(
    ({ report, state: after }: StepResult) => {
      if (!report.ok) {
        if (report.refused && report.refused !== 'already there') say({ kind: 'aside', text: capital(report.refused) + '.' });
        return;
      }
      const out: Line[] = [];
      for (const note of report.notes) {
        if (note.startsWith('mode:')) continue;
        if (note.startsWith('found ')) out.push({ kind: 'prose', text: `You find ${note.slice(6)}.` });
        else if (note.startsWith('worked ') || note.startsWith('ate ') || note.startsWith('slept') || note.startsWith('paid ')) {
          out.push({ kind: 'prose', text: capital(note) + '.' });
        } else if (note === 'the opening problem is settled') out.push({ kind: 'prose', text: "That's settled." });
        else if (note.includes('missed')) out.push({ kind: 'aside', text: 'You missed a meeting.' });
        else if (note.includes('lapsed')) out.push({ kind: 'aside', text: 'A debt came due and went unpaid.' });
      }
      if (report.said) {
        const npc = after.npcs[report.said.npc];
        out.push({ kind: 'said', text: `${npc?.name.split(' ')[0] ?? 'Someone'}: ${report.said.line}` });
      }
      if (report.action.type === 'wait') out.push({ kind: 'aside', text: `${report.elapsed} minutes pass.` });
      if (report.action.type === 'start_scene') {
        const npc = after.npcs[report.action.npc];
        const first = npc?.name.split(' ')[0] ?? 'him';
        out.push({ kind: 'prose', text: report.action.kind === 'shakedown' ? `You step in close to ${first}.` : `You square up to ${first}.` });
      }
      if (report.beat) {
        const first = after.scene ? (after.npcs[after.scene.npc]?.name.split(' ')[0] ?? '') : lastSceneName.current;
        for (const line of report.beat.lines) {
          const text = describeBeat(line, first);
          if (text) out.push({ kind: 'prose', text });
        }
        if (report.beat.end) out.push(...describeEnd(report.beat.end, first).map((text) => ({ kind: 'aside' as const, text })));
      }
      // Arriving somewhere for the first time: the block explains itself
      // by being walked through (§6). The description is content.
      const here = after.player.position.at;
      if (here && !seenPlaces.current.has(here) && (report.action.type === 'travel' || report.action.type === 'move')) {
        seenPlaces.current.add(here);
        const card = content.locations.find((l) => l.id === here);
        if (card) out.push({ kind: 'prose', text: card.description });
      }
      if (out.length > 0) say(...out);
    },
    [say],
  );

  const act = useCallback(
    (action: Action): StepResult => {
      const before = stateRef.current;
      if (before.scene) lastSceneName.current = before.npcs[before.scene.npc]?.name.split(' ')[0] ?? '';
      const result = dispatch(action);
      narrate(result);
      return result;
    },
    [dispatch, narrate],
  );

  /** A talk affordance: the model if it is on, the sim's line if not. */
  const startTalk = (npc: NpcId, minutes: number, fallback: Action) => {
    // Defense in depth, not the primary fix: affordances() no longer offers
    // a talk affordance at all while a conversation is open (sim-side), so
    // this shouldn't be reachable through normal play. It's a backstop
    // against a stale click/closure re-firing this while
    // `activeConversation` is still open for someone — including the same
    // npc — rather than trusting the UI's local `talking` flag, which can
    // itself be null (e.g. after a fault) while the sim still has one open.
    if (stateRef.current.activeConversation !== null) return;
    setWalkingTo(null);
    if (model !== 'on') {
      act(fallback);
      return;
    }
    setTalking(startTalking(npc, minutes));
    void openWith(npc, fallback);
  };

  /**
   * Walk away (§16: proximity is a real move, not held until the model
   * releases you). Closes the conversation in sim state, not just this
   * screen's local view of it — otherwise a reload reconnects to it, which
   * "leaving" should not do.
   */
  const leaveConversation = () => {
    if (stateRef.current.activeConversation !== null) act({ type: 'leave_scene' });
    setTalking(null);
  };

  /**
   * He speaks first (§14). A conversation opens with the other person, not
   * with an empty box: the sim assembles who he is and what is outstanding,
   * the cheap tier says it in his voice, and it costs nothing because
   * nothing has happened yet. If it fails, this is exactly the AI-off path
   * — his card's line, and the minutes it costs.
   */
  const openWith = async (npc: NpcId, fallback: Action) => {
    const ctx = openerContext(stateRef.current, npc, content);
    if (!ctx) {
      act(fallback);
      setTalking(null);
      return;
    }
    setBusy(true);
    const answer = await askModel(ctx, { onProse: streamProse, onReset: streamReset });
    setBusy(false);
    setStreaming('');
    if (!answer.ok) {
      noteFault(answer.fault);
      act(fallback);
      setTalking(null);
      return;
    }
    say({ kind: 'prose', text: answer.contract.prose });
    // He speaks first, on the record (§14): the opener seeds the transcript
    // in sim state, the same place every later exchange writes to.
    act({ type: 'open_scene', npc, line: answer.contract.prose });
  };

  const sendLine = async (text: string) => {
    const t = talkingRef.current;
    if (!t || busy) return;
    const current = stateRef.current;
    const npc = current.npcs[t.npc];
    const first = npc?.name.split(' ')[0] ?? 'him';
    // The first exchange costs what you chose; the rest, a word each (§9).
    const minutes = billedMinutes(t, TUNING.talk.wordMinutes);
    const transcript = current.activeConversation?.npc === t.npc ? current.activeConversation.transcript : [];
    const ctx = renderContext(current, { npc: t.npc, playerText: text, transcript, minutes }, content);
    if (!ctx) {
      say({ kind: 'aside', text: `${first} is gone.` });
      setTalking(null);
      return;
    }
    setBusy(true);
    say({ kind: 'said', text: `You: ${text}` });
    const answer = await askModel(ctx, { onProse: streamProse, onReset: streamReset });
    setBusy(false);
    setStreaming('');
    if (!answer.ok) {
      // Fun with the AI off (§1): the sim's line, and the time still passes.
      noteFault(answer.fault);
      act({ type: 'talk', npc: t.npc, minutes });
      setTalking(null);
      return;
    }
    const result = act({ type: 'scene', npc: t.npc, minutes, playerText: text, contract: answer.contract });
    if (!result.report.ok) {
      setTalking(null);
      return;
    }
    if (result.report.dropped.length > 0) console.warn('outcomes dropped by the sim', result.report.dropped);
    say({ kind: 'prose', text: answer.contract.prose });
    if (answer.contract.scene_end) {
      setTalking(null);
      return;
    }
    setTalking(afterExchange(t));
  };

  // The nightly digest (§19): one batched call for the day that just ended,
  // when there is a record worth telling and a model to tell it.
  const digested = useRef(initial.time.day);
  useEffect(() => {
    const day = state.time.day;
    if (day <= digested.current) return;
    const yesterday = day - 1;
    digested.current = day;
    if (modelStatus() !== 'on') return;
    const ctx = digestContext(stateRef.current, yesterday, content);
    // Nobody made anything of the day: there is no news, so there is no
    // digest. Reactions are the subject, not a recap of what you did.
    if (ctx.reactions.length === 0) return;
    void askModel(ctx).then((answer) => {
      if (!answer.ok) return;
      const result = act({ type: 'digest', day: yesterday, prose: answer.contract.prose });
      if (result.report.ok) say({ kind: 'prose', text: answer.contract.prose });
    });
  }, [state.time.day, act, say]);

  // Walking: joystick vectors and tap-to-walk targets both become `move`
  // actions, a step at a time, so the sim charges every minute of it.
  useEffect(() => {
    const id = window.setInterval(() => {
      const current = stateRef.current;
      if (current.scene || talkingRef.current) return; // nobody walks away mid-scene except by running
      const you = current.player.position.point;
      const v = vector.current;
      if (v) {
        // The map view is turned a quarter turn from world space (Map.tsx:
        // `rotate(90)`, world (x, y) -> screen (-y, x)), but the joystick's
        // vector is a raw screen-space drag. Un-rotate it before treating it
        // as a world delta, or "up" on the stick walks you sideways on the
        // map.
        act({ type: 'move', to: { x: you.x + v.y * WALK_STEP, y: you.y - v.x * WALK_STEP } });
        return;
      }
      if (walkingTo) {
        const d = Math.hypot(walkingTo.x - you.x, walkingTo.y - you.y);
        if (d <= ARRIVED) {
          setWalkingTo(null);
          return;
        }
        const s = Math.min(WALK_STEP, d);
        act({ type: 'move', to: { x: you.x + ((walkingTo.x - you.x) / d) * s, y: you.y + ((walkingTo.y - you.y) / d) * s } });
      }
    }, WALK_TICK_MS);
    return () => window.clearInterval(id);
  }, [act, walkingTo]);

  const needs = needsStatus(state);
  const options = affordances(state);

  const onBuilding = (id: string) => {
    setWalkingTo(null);
    const known = state.player.knownLocations.includes(id as never);
    if (known && state.player.position.at !== id) {
      act({ type: 'travel', to: id as never });
    } else if (!known) {
      const door = state.locations[id as keyof typeof state.locations]?.entrance;
      if (door) setWalkingTo(door);
    } else {
      markHintSeen();
    }
  };

  const onGround = (p: MapPoint) => {
    markHintSeen();
    if (talking) return;
    setWalkingTo(p);
  };

  const startOver = () => {
    if (!window.confirm('Leave this run behind and start a new one?')) return;
    void clearSave().then(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete('seed');
      window.location.assign(url.toString());
    });
  };

  const how = [needs.hunger === 'fed' ? '' : needs.hunger, needs.fatigue === 'rested' ? '' : needs.fatigue]
    .filter((s) => s !== '')
    .join(', ');

  const sheetHeight = '40vh';

  return (
    <div className="play" style={{ ['--sheet-height' as never]: sheetHeight }}>
      <Map
        state={state}
        map={content.map}
        onBuilding={onBuilding}
        onGround={onGround}
        onNpc={(id) => {
          markHintSeen();
          setReading(id);
        }}
        walkingTo={walkingTo}
      />

      <div className="tab" aria-live="polite">
        <div className="when">{clock(state.time)}</div>
        <div>${state.player.money.toFixed(2)}</div>
        {how && <div className="how">{how}</div>}
        <div className="district">
          {state.districtName}
          {' · '}
          <button type="button" className="link" onClick={startOver}>
            new run
          </button>
          {model !== 'on' && model !== 'unknown' && (
            <>
              {' · '}
              <a className="link" href="/api/health" target="_blank" rel="noreferrer">
                {faultLabel(model)}
              </a>
            </>
          )}
        </div>
      </div>

      {!state.scene && !talking && (
        <Joystick
          onVector={(v) => {
            vector.current = v;
            if (v) {
              setWalkingTo(null);
              markHintSeen();
            }
          }}
        />
      )}

      <section className="sheet" aria-label="What's happening">
        <div className="grip" />
        <div className="lines" ref={linesEl}>
          {lines.map((l, i) => (
            <p key={i} className={l.kind === 'prose' ? undefined : l.kind}>
              {l.text}
            </p>
          ))}
          {streaming !== '' && <p className="live">{streaming}</p>}
        </div>
        {talking ? (
          <Talk
            name={state.npcs[talking.npc]?.name.split(' ')[0] ?? 'them'}
            busy={busy}
            onSend={(text) => void sendLine(text)}
            onLeave={leaveConversation}
          />
        ) : (
          <div className="choices">
            {options
              .filter((o) => o.kind !== 'fight' && o.kind !== 'shakedown')
              .map((o) => (
                <button
                  key={label(o, state)}
                  type="button"
                  className={`choice${o.kind === 'wait' ? ' quiet' : ''}`}
                  onClick={() => {
                    if (o.kind === 'talk') {
                      startTalk(o.npc.id, o.minutes, o.action);
                      return;
                    }
                    setWalkingTo(null);
                    act(o.action);
                  }}
                >
                  {label(o, state)}
                </button>
              ))}
          </div>
        )}
      </section>

      {reading && (
        <ReadScreen
          state={state}
          npc={reading}
          onClose={() => setReading(null)}
          starters={options
            .filter((o) => (o.kind === 'fight' || o.kind === 'shakedown') && o.npc.id === reading)
            .map((o) => ({ label: label(o, state), action: o.action }))}
          onAct={(action) => {
            setReading(null);
            setWalkingTo(null);
            act(action);
          }}
        />
      )}
    </div>
  );
}

function label(o: ReturnType<typeof affordances>[number], state: WorldState): string {
  switch (o.kind) {
    case 'talk': {
      const first = o.npc.name.split(' ')[0];
      return o.minutes >= 60 ? `Sit with ${first} a while` : `A word with ${first}`;
    }
    case 'pay': {
      const to = o.creditor.name.split(' ')[0];
      const forWhom = o.fact.from === 'player' ? '' : ` for ${state.npcs[o.fact.from]?.name.split(' ')[0] ?? 'them'}`;
      return `Pay ${to} $${o.amount}${forWhom}`;
    }
    case 'eat':
      return `Eat ($${o.price.toFixed(2)})`;
    case 'sleep':
      return o.inBed ? 'Sleep' : 'Sleep rough';
    case 'work':
      return `Work: ${o.job.name}`;
    case 'wait':
      return 'Wait half an hour';
    case 'fight':
      return `Start something with ${o.npc.name.split(' ')[0]}`;
    case 'shakedown':
      return `Lean on ${o.npc.name.split(' ')[0]} for $${o.amount}`;
    case 'beat':
      return verbLabel(o.verb, state.scene ? (state.npcs[state.scene.npc]?.name.split(' ')[0] ?? 'him') : 'him');
  }
}

/** The swapped verb list (§17), in words. */
function verbLabel(verb: ViolenceVerb, name: string): string {
  switch (verb) {
    case 'press':
      return `Crowd ${name}`;
    case 'swing':
      return 'Swing';
    case 'grab':
      return `Grab ${name}`;
    case 'talk_down':
      return `Talk ${name} down`;
    case 'back_off':
      return 'Back off';
    case 'run':
      return 'Run';
    case 'demand':
      return 'Name the number';
    case 'let_go':
      return 'Let it go';
  }
}

/** A beat line, in words. Data in, one sentence out; null for the lines that say nothing new. */
function describeBeat(line: BeatLine, name: string): string | null {
  const you = line.who === 'player';
  switch (line.act) {
    case 'swings_and_lands':
      return you ? 'You swing, and it lands.' : `${name} swings, and it lands.`;
    case 'swings_and_misses':
      return you ? 'You swing and miss.' : `${name} swings. You get out of the way.`;
    case 'grabs':
      return `You get a hold of ${name}.`;
    case 'shakes_free':
      return `${name} shakes free.`;
    case 'presses':
      return `You crowd ${name}.`;
    case 'holds_ground':
      return `${name} doesn't move.`;
    case 'wavers':
      return `${name} wavers.`;
    case 'staggers':
      return you ? 'You stagger.' : `${name} staggers.`;
    case 'goes_down':
      return you ? 'You go down.' : `${name} goes down.`;
    case 'yields':
      return `${name} folds.`;
    case 'pays':
      return `${name} hands over $${line.amount}.`;
    case 'refuses':
      return `${name} says no, and means it.`;
    case 'runs':
      return you ? 'You run.' : `${name} runs.`;
    case 'backs_off':
      return you ? 'You back off.' : `${name} backs off.`;
    case 'talks':
      return 'You talk, low and even.';
    case 'listens':
      return `${name} listens. That's the end of it.`;
    case 'demands':
      return 'You name the number.';
    case 'lets_go':
      return 'You let it go.';
    case 'keeps_going':
      return `${name} is down and your hands are still moving.`;
    case 'escalates':
      if (line.why === 'swung_first' && line.who === 'npc') return "It's a fight now. Nobody at this door chose that.";
      if (line.why === 'temper') return "You don't remember deciding to keep going.";
      return null;
  }
}

function describeEnd(end: SceneEnd, name: string): string[] {
  const out: string[] = [];
  if (end.became === 'fistfight') out.push(end.startedAs === 'scare' ? 'What started as a lean ended as a fight.' : 'It was a fight, and everyone here saw it.');
  else out.push(end.paid > 0 ? `${name} paid.` : `${name} didn't pay.`);
  if (end.targetInjury) out.push(`${name} is leaving with a ${end.targetInjury}.`);
  if (end.playerInjury) out.push(`You're leaving with a ${end.playerInjury}.`);
  return out;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** "Monday, 10:40 pm". The sim's `formatTime` is for transcripts. */
function clock(t: WorldTime): string {
  const hour24 = Math.floor(t.minute / 60);
  const minute = String(t.minute % 60).padStart(2, '0');
  const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${WEEKDAYS[dayOfWeek(t)]}, ${hour}:${minute} ${hour24 < 12 ? 'am' : 'pm'}`;
}

function capital(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
